import styled, { keyframes } from 'styled-components';

import { Text, TextProps } from '../../cdl/Text/Text';
import { ColorPath } from '../ui/colors';
import { theme } from '../ui/theme';

const shimmerAnimation = keyframes`
    0% {
        background-position: 200% 0;
    }
    100% {
        background-position: -200% 0;
    }
`;

interface StyledTextShimmerProps extends Omit<TextProps, 'color'> {
    $color?: ColorPath;
}

const getThemeColor = (colorPath: ColorPath) => {
    const [groupKey, shadeKey] = colorPath.split('.') as [keyof typeof theme.colors, string | undefined];
    const group = theme.colors[groupKey];

    if (typeof group === 'string') {
        return group;
    }

    return group[shadeKey as keyof typeof group];
};

const StyledTextShimmer = styled(Text)<StyledTextShimmerProps>`
    display: inline-block;
    background: linear-gradient(
        110deg,
        ${({ $color }) => getThemeColor($color ?? 'foreground.default')} 0%,
        ${({ $color }) => getThemeColor($color ?? 'foreground.default')} 20%,
        ${theme.colors.foreground.onEmphasis} 48%,
        ${theme.colors.foreground.onEmphasis} 52%,
        ${({ $color }) => getThemeColor($color ?? 'foreground.default')} 80%,
        ${({ $color }) => getThemeColor($color ?? 'foreground.default')} 100%
    );
    background-size: 200% 100%;
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    color: ${theme.colors.foreground.default};
    animation: ${shimmerAnimation} 2s linear infinite;
`;

export interface TextShimmerProps extends Omit<TextProps, 'color'> {
    children: string[] | string;
    color?: ColorPath;
}

export const TextShimmer = ({ children, color = 'foreground.default', ...rest }: TextShimmerProps) => {
    return (
        <StyledTextShimmer $color={color} {...rest}>
            {children}
        </StyledTextShimmer>
    );
};
